/**
 * The ABF Companion Program Incident Report, as steps on the phone with the outing's facts shown at the top
 * (date, time, tenant, volunteer). Required (the insurance items): type; what happened; injuries, severity,
 * medical attention and where; actions taken (911, emergency contacts, first aid, office notified and who);
 * is the tenant okay now; did the outing end early / the time you returned; the typed name. Optional:
 * individuals and staff involved, witnesses (add another) and their statements, hazards / negligence /
 * protocols, property damage, follow-up with an owner, and photos of a hazard or damage — never of people.
 * 17 Oct 2026: a photo is said as added only when the server took it; one that did not upload is said plainly, with
 * Try again for those photos only (the report itself stays sent).
 */
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanionIncidentSchema, INCIDENT_TYPES, SEVERITIES, type CompanionIncidentInput } from '@shared/volunteers';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { ChecksField, ChoiceField, SelectField, TextArea, TextField, toErrors, type Errors } from '@/features/people/fields';
import { Button } from '@/components/ui/button';

type Step = 'what' | 'injuries' | 'actions' | 'after' | 'more' | 'sign';
const STEPS: Step[] = ['what', 'injuries', 'actions', 'after', 'more', 'sign'];
/** Which fields each step owns (for the per-step check). */
const OWNS: Record<Step, string[]> = {
  what: ['types', 'typeOther', 'location', 'whatHappened'],
  injuries: ['injuries', 'injuryDescription', 'severity', 'medical', 'medicalWhere'],
  actions: ['called911', 'emergencyContacts', 'firstAid', 'officeNotified', 'officeNotifiedWho'],
  after: ['tenantOkayNow', 'endedEarly', 'returnedAt'],
  more: ['followUpOwner'],
  sign: ['typedName'],
};

export interface IncidentFacts {
  day: string;
  time: string;
  tenant: string;
  volunteer: string;
  activity: string;
}

export function IncidentForm({
  facts,
  onSubmit,
  busy,
  onPhotos,
  headingLevel = 2,
}: {
  facts: IncidentFacts;
  onSubmit: (r: CompanionIncidentInput) => Promise<boolean>;
  busy?: boolean;
  /**
   * After submitting: add photos of a hazard or damage (optional). 17 Oct 2026: resolves with the photos the server did
   * NOT take (empty: every one was added) — only those are said as added, the others are offered again (Try again).
   */
  onPhotos?: (files: File[]) => Promise<File[]>;
  headingLevel?: 1 | 2 | 3;
}) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const uid = useId();
  const [step, setStep] = useState<Step>('what');
  const [r, setR] = useState<Partial<CompanionIncidentInput>>({ types: [], witnesses: [] });
  const [errs, setErrs] = useState<Errors>({});
  const [sent, setSent] = useState(false);
  const [photoNote, setPhotoNote] = useState<{ text: string; bad: boolean } | null>(null);
  /** 17 Oct 2026: the photos the server did not take — Try again sends these, and only these, again. */
  const [unsent, setUnsent] = useState<File[]>([]);
  const [sendingPhotos, setSendingPhotos] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const first = useRef(true);
  const H = (`h${headingLevel}` as 'h1' | 'h2' | 'h3');
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    heading.current?.focus();
  }, [step, sent]);

  const set = (k: keyof CompanionIncidentInput, v: unknown) => setR((x) => ({ ...x, [k]: v }));
  const yn = [
    { value: 'yes' as const, label: t('vol.yes') },
    { value: 'no' as const, label: t('vol.no') },
  ];
  const e = (k: string) => (errs[k] ? 'required' : undefined);

  const checkStep = (s: Step): Errors => {
    const p = CompanionIncidentSchema.safeParse(r);
    if (p.success) return {};
    const all = toErrors(p.error);
    const idx = STEPS.indexOf(s);
    const owned = STEPS.slice(0, idx + 1).flatMap((x) => OWNS[x]);
    return Object.fromEntries(Object.entries(all).filter(([k]) => owned.includes(k.split('.')[0]!)));
  };

  const next = async () => {
    const found = checkStep(step);
    if (Object.keys(found).length) {
      setErrs(found);
      announce(t('vol.form.fixErrors'), 'assertive');
      return;
    }
    setErrs({});
    const i = STEPS.indexOf(step);
    if (step === 'sign') {
      const p = CompanionIncidentSchema.safeParse(r);
      if (!p.success) {
        setErrs(toErrors(p.error));
        return;
      }
      if (await onSubmit(r as CompanionIncidentInput)) setSent(true);
      return;
    }
    setStep(STEPS[i + 1]!);
  };

  const witnesses = r.witnesses ?? [];
  const setWitness = (i: number, k: string, v: string) => set('witnesses', witnesses.map((w, j) => (j === i ? { ...w, [k]: v } : w)));

  /** 17 Oct 2026: a photo is said as added only when the server took it; one that did not upload is said, and kept for Try again. */
  const sendPhotos = async (files: File[]) => {
    if (!onPhotos || !files.length) return;
    const others = unsent.filter((f) => !files.includes(f));
    setSendingPhotos(true);
    let notAdded: File[];
    try {
      notAdded = await onPhotos(files);
    } catch {
      notAdded = files;
    }
    setSendingPhotos(false);
    const left = [...others, ...notAdded];
    setUnsent(left);
    const added = files.length - notAdded.length;
    const text = [left.length ? t('vol.inc.photosFailed', { count: left.length }) : '', added ? t('vol.inc.photosAdded', { count: added }) : ''].filter(Boolean).join(' ');
    setPhotoNote({ text, bad: left.length > 0 });
    // one message (the announcer keeps the last of two said at once)
    announce(text, left.length ? 'assertive' : 'polite');
  };

  if (sent) {
    return (
      <div className="incident-form" data-testid="incident-sent">
        <H ref={heading} tabIndex={-1}>
          {t('vol.inc.sentTitle')}
        </H>
        <p>{t('vol.inc.sentBody')}</p>
        {onPhotos ? (
          <div className="field">
            <p>
              <b>{t('vol.inc.photosLine')}</b>
            </p>
            <p className="help">{t('vol.inc.photosRule')}</p>
            <label className="btn secondary" htmlFor={`${uid}-photos`}>
              <Icon name="camera" /> {t('vol.inc.addPhotos')}
            </label>
            <input
              id={`${uid}-photos`}
              className="sr-only"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              multiple
              disabled={sendingPhotos}
              onChange={(ev) => {
                const files = [...(ev.target.files ?? [])];
                ev.target.value = '';
                void sendPhotos(files);
              }}
              data-testid="incident-photos"
            />
            {photoNote ? (
              <p className={photoNote.bad ? 'field-err' : 'help'} data-testid="incident-photos-note">
                {photoNote.text}
              </p>
            ) : null}
            {unsent.length ? (
              <Button plain type="button" className="btn secondary" disabled={sendingPhotos} onClick={() => sendPhotos(unsent)} data-testid="incident-photos-retry">
                {t('vol.inc.photosRetry')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  const i = STEPS.indexOf(step);
  return (
    <div className="incident-form" data-testid="incident-form" data-step={step}>
      <div className="trans" aria-label={t('vol.inc.factsLabel')}>
        <span className="lbl">{t('vol.inc.confidential')}</span>
        <p>
          {facts.day} · {facts.time} · {facts.activity}
          <br />
          {t('vol.inc.factsLine', { tenant: facts.tenant, volunteer: facts.volunteer })}
        </p>
      </div>
      <ol className="vsteps" aria-label={t('vol.form.stepsLabel', { n: i + 1, of: STEPS.length })}>
        {STEPS.map((s, j) => (
          <li key={s}>
            <span className={s === step ? 'on' : undefined} aria-current={s === step ? 'step' : undefined}>
              {j + 1} {t(`vol.inc.step.${s}`)}
            </span>
          </li>
        ))}
      </ol>
      <H ref={heading} tabIndex={-1}>
        {t(`vol.inc.step.${step}`)}
      </H>
      {step === 'what' ? (
        <>
          <ChecksField id={`${uid}-types`} label={t('vol.inc.types')} required values={(r.types ?? []) as (typeof INCIDENT_TYPES)[number][]} onChange={(v) => set('types', v)} options={INCIDENT_TYPES.map((x) => ({ value: x, label: t(`vol.inc.type.${x}`) }))} error={e('types')} />
          {(r.types ?? []).includes('other') ? <TextField id={`${uid}-to`} label={t('vol.inc.typeOther')} required value={r.typeOther ?? ''} onChange={(v) => set('typeOther', v)} error={e('typeOther')} /> : null}
          <TextField id={`${uid}-loc`} label={t('vol.inc.location')} required value={r.location ?? ''} onChange={(v) => set('location', v)} error={e('location')} />
          <TextField id={`${uid}-weather`} label={t('vol.inc.weather')} value={r.weather ?? ''} onChange={(v) => set('weather', v)} />
          <TextArea id={`${uid}-what`} label={t('vol.inc.whatHappened')} required value={r.whatHappened ?? ''} onChange={(v) => set('whatHappened', v)} error={e('whatHappened')} rows={5} />
        </>
      ) : null}
      {step === 'injuries' ? (
        <>
          <ChoiceField id={`${uid}-inj`} label={t('vol.inc.injuries')} required value={r.injuries ?? ''} onChange={(v) => set('injuries', v)} options={yn} error={e('injuries')} />
          {r.injuries === 'yes' ? (
            <>
              <TextArea id={`${uid}-injd`} label={t('vol.inc.injuryDescription')} required value={r.injuryDescription ?? ''} onChange={(v) => set('injuryDescription', v)} error={e('injuryDescription')} />
              <SelectField id={`${uid}-sev`} label={t('vol.inc.severity')} required value={(r.severity ?? '') as (typeof SEVERITIES)[number] | ''} onChange={(v) => set('severity', v)} options={SEVERITIES.map((s) => ({ value: s, label: t(`vol.inc.sev.${s}`) }))} blank={t('vol.inc.choose')} error={e('severity')} />
            </>
          ) : null}
          <ChoiceField id={`${uid}-med`} label={t('vol.inc.medical')} required value={r.medical ?? ''} onChange={(v) => set('medical', v)} options={yn} error={e('medical')} />
          {r.medical === 'yes' ? <TextField id={`${uid}-medw`} label={t('vol.inc.medicalWhere')} required value={r.medicalWhere ?? ''} onChange={(v) => set('medicalWhere', v)} error={e('medicalWhere')} /> : null}
          <ChoiceField id={`${uid}-dmg`} label={t('vol.inc.propertyDamage')} value={(r.propertyDamage ?? '') as 'yes' | 'no' | ''} onChange={(v) => set('propertyDamage', v)} options={yn} />
          {r.propertyDamage === 'yes' ? <TextArea id={`${uid}-dmgw`} label={t('vol.inc.propertyDamageWhat')} value={r.propertyDamageWhat ?? ''} onChange={(v) => set('propertyDamageWhat', v)} /> : null}
        </>
      ) : null}
      {step === 'actions' ? (
        <>
          <ChoiceField id={`${uid}-911`} label={t('vol.inc.called911')} required value={r.called911 ?? ''} onChange={(v) => set('called911', v)} options={yn} error={e('called911')} />
          <ChoiceField id={`${uid}-ec`} label={t('vol.inc.emergencyContacts')} required value={r.emergencyContacts ?? ''} onChange={(v) => set('emergencyContacts', v)} options={yn} error={e('emergencyContacts')} />
          {r.emergencyContacts === 'yes' ? <TextField id={`${uid}-ecw`} label={t('vol.inc.emergencyContactsWho')} value={r.emergencyContactsWho ?? ''} onChange={(v) => set('emergencyContactsWho', v)} /> : null}
          <ChoiceField id={`${uid}-fa`} label={t('vol.inc.firstAid')} required value={r.firstAid ?? ''} onChange={(v) => set('firstAid', v)} options={yn} error={e('firstAid')} />
          {r.firstAid === 'yes' ? <TextField id={`${uid}-faw`} label={t('vol.inc.firstAidWhat')} value={r.firstAidWhat ?? ''} onChange={(v) => set('firstAidWhat', v)} /> : null}
          <ChoiceField id={`${uid}-on`} label={t('vol.inc.officeNotified')} required value={r.officeNotified ?? ''} onChange={(v) => set('officeNotified', v)} options={yn} error={e('officeNotified')} />
          {r.officeNotified === 'yes' ? <TextField id={`${uid}-onw`} label={t('vol.inc.officeNotifiedWho')} required value={r.officeNotifiedWho ?? ''} onChange={(v) => set('officeNotifiedWho', v)} error={e('officeNotifiedWho')} /> : null}
        </>
      ) : null}
      {step === 'after' ? (
        <>
          <ChoiceField id={`${uid}-ok`} label={t('vol.inc.tenantOkayNow')} required value={r.tenantOkayNow ?? ''} onChange={(v) => set('tenantOkayNow', v)} options={yn} error={e('tenantOkayNow')} />
          <TextField id={`${uid}-okn`} label={t('vol.inc.tenantOkayNote')} value={r.tenantOkayNote ?? ''} onChange={(v) => set('tenantOkayNote', v)} />
          <ChoiceField id={`${uid}-early`} label={t('vol.inc.endedEarly')} required value={r.endedEarly ?? ''} onChange={(v) => set('endedEarly', v)} options={yn} error={e('endedEarly')} />
          <TextField id={`${uid}-ret`} label={t('vol.inc.returnedAt')} required value={r.returnedAt ?? ''} onChange={(v) => set('returnedAt', v)} error={e('returnedAt')} placeholder="2:40" inputMode="numeric" />
        </>
      ) : null}
      {step === 'more' ? (
        <>
          <p className="help">{t('vol.inc.optional')}</p>
          <TextField id={`${uid}-ind`} label={t('vol.inc.individuals')} value={r.individuals ?? ''} onChange={(v) => set('individuals', v)} />
          <TextField id={`${uid}-staff`} label={t('vol.inc.staffInvolved')} value={r.staffInvolved ?? ''} onChange={(v) => set('staffInvolved', v)} />
          <fieldset className="field">
            <legend>{t('vol.inc.witnesses')}</legend>
            {witnesses.map((w, j) => (
              <div className="witness" key={j}>
                <TextField id={`${uid}-wn${j}`} label={t('vol.inc.witnessName', { n: j + 1 })} value={w.name ?? ''} onChange={(v) => setWitness(j, 'name', v)} />
                <div className="grid-2">
                  <TextField id={`${uid}-wp${j}`} label={t('vol.inc.witnessPhone')} value={w.phone ?? ''} onChange={(v) => setWitness(j, 'phone', v)} type="tel" />
                  <TextField id={`${uid}-we${j}`} label={t('vol.inc.witnessEmail')} value={w.email ?? ''} onChange={(v) => setWitness(j, 'email', v)} type="email" />
                </div>
                <TextArea id={`${uid}-ws${j}`} label={t('vol.inc.witnessStatement')} value={w.statement ?? ''} onChange={(v) => setWitness(j, 'statement', v)} />
              </div>
            ))}
            {witnesses.length < 6 ? (
              <button type="button" className="btn secondary sm" onClick={() => set('witnesses', [...witnesses, { name: '', phone: '', email: '', statement: '' }])} data-testid="incident-add-witness">
                <Icon name="plus" /> {witnesses.length ? t('vol.inc.addAnother') : t('vol.inc.addWitness')}
              </button>
            ) : null}
          </fieldset>
          {(['hazards', 'negligence', 'protocols'] as const).map((k) => (
            <div key={k}>
              <ChoiceField id={`${uid}-${k}`} label={t(`vol.inc.${k}`)} value={(r[k] ?? '') as 'yes' | 'no' | ''} onChange={(v) => set(k, v)} options={yn} />
              {r[k] ? <TextArea id={`${uid}-${k}x`} label={t('vol.inc.explain')} value={String(r[`${k}Explain` as keyof CompanionIncidentInput] ?? '')} onChange={(v) => set(`${k}Explain` as keyof CompanionIncidentInput, v)} /> : null}
            </div>
          ))}
          <ChoiceField id={`${uid}-fu`} label={t('vol.inc.followUp')} value={(r.followUp ?? '') as 'yes' | 'no' | ''} onChange={(v) => set('followUp', v)} options={yn} help={t('vol.inc.followUpHelp')} />
          {r.followUp === 'yes' ? (
            <>
              <TextField id={`${uid}-fuw`} label={t('vol.inc.followUpWhat')} value={r.followUpWhat ?? ''} onChange={(v) => set('followUpWhat', v)} />
              <TextField id={`${uid}-fuo`} label={t('vol.inc.followUpOwner')} required value={r.followUpOwner ?? ''} onChange={(v) => set('followUpOwner', v)} error={e('followUpOwner')} />
            </>
          ) : null}
        </>
      ) : null}
      {step === 'sign' ? (
        <div className="signbox">
          <TextField id={`${uid}-name`} label={t('vol.inc.typedName')} required value={r.typedName ?? ''} onChange={(v) => set('typedName', v)} error={errs.typedName ? 'typedName' : undefined} autoComplete="off" help={t('vol.inc.typedHelp')} />
        </div>
      ) : null}
      <div className="vs-nav">
        {i > 0 ? (
          <button type="button" className="big-btn secondary" onClick={() => setStep(STEPS[i - 1]!)} disabled={busy}>
            {t('vol.form.back')}
          </button>
        ) : null}
        <Button plain type="button" className="big-btn" onClick={() => next()} disabled={busy} data-testid="incident-next">
          {step === 'sign' ? t('vol.inc.submit') : t('vol.form.next')}
        </Button>
      </div>
    </div>
  );
}
